import React, { useEffect, useRef, useState } from "react";
import AllFilters from "./AllFilters";
import { updateCompanyDirectory } from "utils/searchUtils";
import { formatDateRangeLabel } from "utils/dateFilterUtils";
import {
  filterCompanyDirectory,
} from "actions/companyDirectoryActions";
import { useDispatch, useSelector } from "react-redux";

const HIDDEN_CHIP_FILTERS = [
  "isApproved",
  "isDeleted",
  "approvedByUserId",
  "category",
  "vendorCategory",
  "tags",
];

const formatFinancialYearChip = (value = "") => {
  const [startYear, endYear] = String(value).split("-");
  return startYear && endYear ? `FY ${startYear}-${endYear.slice(-2)}` : value;
};

const FilterSection = ({ activeTab, onClose }) => {
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const auth = useSelector((state) => state.auth);
  const [chips, setChips] = useState([]);
  const companyDirectory = useSelector((state) => state.companyDirectory);

  const userProfile = auth?.user;
  const userId = userProfile?.userId;
  const entityId = userProfile?.userEntity?.entityId;
  const appliedFilters = companyDirectory?.appliedFilters;
  const searchKey = companyDirectory?.searchKey;
  const removeChips = ({ key, value, isMultiple }) => {
    if (key === "gstPanAvailability") {
      const filterData = { ...appliedFilters };
      delete filterData.gstPanAvailability;
      delete filterData.gstNumberAvailability;
      delete filterData.panNumberAvailability;

      dispatch(
        filterCompanyDirectory({ filterData, searchKey, userId, entityId })
      );
      return;
    }

    if (key === "totalOrderValue") {
      const filterData = { ...appliedFilters };
      delete filterData.totalOrderValue;

      dispatch(
        filterCompanyDirectory({ filterData, searchKey, userId, entityId })
      );
      return;
    }

    const filterData = updateCompanyDirectory({
      field: key,
      value,
      isMultiple,
      appliedFilters,
    });

    if (key === "location") {
      delete filterData.area;
    }

    dispatch(
      filterCompanyDirectory({ filterData, searchKey, userId, entityId })
    );
  };

  const handleClearFilter = () => {
    const filterData = activeTab === "MY_VENDORS"
      ? { isApproved: true, createdBy: userId }
      : activeTab === "APPROVED"
        ? { isApproved: true }
        : {};

    dispatch(
      filterCompanyDirectory({ filterData, searchKey, userId, entityId })
    );
  };
  useEffect(() => {
    let chipsData = [];
    const gstValue = appliedFilters?.gstNumberAvailability;
    const panValue = appliedFilters?.panNumberAvailability;

    Object.keys(appliedFilters || {}).forEach((key) => {
      if (HIDDEN_CHIP_FILTERS.includes(key)) return;
      if (activeTab === "MY_VENDORS" && key === "createdBy") return;
      if (key === "gstNumberAvailability" || key === "panNumberAvailability") {
        return;
      }

      const value = appliedFilters[key];

      if (Array.isArray(value)) {
        chipsData.push(
          ...value.map((v) => ({
            key,
            value: v,
            isMultiple: true,
          }))
        );
      } else if (value !== undefined && value !== null && value !== "") {
        chipsData.push({
          key,
          value,
          isMultiple: false,
        });
      }
    });

    if (gstValue === "with" && panValue === "with" && !appliedFilters?.gstPanAvailability) {
      chipsData.push({
        key: "gstPanAvailability",
        value: "with",
        isMultiple: false,
      });
    }

    if (gstValue === "without" && panValue === "without" && !appliedFilters?.gstPanAvailability) {
      chipsData.push({
        key: "gstPanAvailability",
        value: "without",
        isMultiple: false,
      });
    }

    setChips(chipsData);
  }, [activeTab, appliedFilters]);

  const getChipLabel = (chip) => {
    if (chip.key === "isPremium") return "Promoted Profiles";
    if (chip.key === "isNeevayVendor") return "On Neevay";
    if (chip.key === "gstPanAvailability") {
      return chip.value === "with"
        ? "With GSTIN or PAN"
        : "Without GSTIN or PAN";
    }
    if (chip.key === "vendorActivity") return chip.value;
    if (chip.key === "financialYear") return formatFinancialYearChip(chip.value);
    if (chip.key === "vendorInvitationCount") {
      const labels = {
        0: "Non-invited Vendors",
        invited: "Invited Vendors",
        1: "Invited once (1)",
        "2-5": "2–5 invitations",
        "6-10": "6–10 invitations",
        "11+": "11+ invitations",
      };
      return labels[chip.value] || chip.value;
    }
    if (chip.key === "totalOrderValue") {
      return chip?.value?.label || "Total Order Value";
    }

    if (chip.key === "createdDate") {
      return formatDateRangeLabel(
        chip?.value?.startDate,
        chip?.value?.endDate
      );
    }

    return chip?.value;
  };

  return (
    <div
      style={{
        height: "100%",
        scrollbarWidth: "none",
        "-ms-overflow-style": "none",
      }}
      ref={containerRef}
      className="overflow-auto bg-white w-[224px] rounded-xl border border-[#E8E8E0] shadow-sm"
    >
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-[#E8E8E0] bg-white px-3 py-2.5">
        <p className="text-sm font-bold text-[#1F2937]">Filters</p>
        <div className="flex items-center gap-2">
          {chips?.length >= 1 && (
            <button
              type="button"
              onClick={handleClearFilter}
              className="text-[11px] font-semibold text-[#E95420] hover:underline"
            >
              Clear All
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-[#7A7A68] hover:bg-[#F2F2EA] hover:text-[#303026]"
            aria-label="Close filters"
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 18L18 6M6 6l12 12"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
              />
            </svg>
          </button>
        </div>
      </div>

      {chips?.length > 0 && (
        <div className="sticky top-[42px] z-10 flex flex-wrap gap-1.5 border-b border-[#F0F0E8] bg-white px-2 py-2">
          {chips.map((chip) => (
            <div
              key={`${chip.key}-${chip.value?.label || chip.value}`}
              className={`flex items-center rounded-full border border-[#F7B99D] bg-[#FFF4EE] px-2 py-[3px] ${
                chip.key === "createdDate" ? "max-w-full" : "max-w-32"
              }`}
            >
              <span
                className={`text-[#B43A12] ${
                  chip.key === "createdDate"
                    ? "text-[11px] leading-4 whitespace-normal break-words"
                    : "truncate text-[11px]"
                }`}
                title={getChipLabel(chip)}
              >
                {getChipLabel(chip)}
              </span>
              <button
                onClick={() => removeChips(chip)}
                className="ml-1 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-[#B43A12] hover:bg-[#FAD8C8] focus:outline-none"
                type="button"
              >
                <svg
                  className="h-2 w-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <AllFilters activeTab={activeTab} containerRef={containerRef} />
    </div>
  );
};

export default FilterSection;
